import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

import {interpret, Machine} from 'xstate'


const smileMachine = Machine({
    key: "smile",
    initial: "upload",
    states: {
        upload: {
            on: { NEXT: "alignExtraOral" },
            meta: { title: "Upload photos", category: "upload" }
        },

        alignExtraOral: {
            on: { NEXT: "alignIntraOral" },
            meta: { title: "Align extraoral photo", category: "align" }
        },
        alignIntraOral: {
            on: { PREV: "alignExtraOral", NEXT: "alignReferenceLine" },
            meta: { title: "Align intraoral photo", category: "align" }
        },
        alignReferenceLine: {
            on: { PREV: "alignIntraOral", NEXT: "drawZenith" },
            meta: { title: "Align horizontal reference line", category: "align" }
        },

        drawZenith: {
            on: { PREV: "alignReferenceLine", NEXT: "drawIncisalEdge" },
            meta: { title: "Draw zenith points", category: "draw" }
        },
        drawIncisalEdge: {
            on: { PREV: "drawZenith", NEXT: "drawContour" },
            meta: { title: "Draw incisal edge points", category: "draw" }
        },
        drawContour: {
            on: { PREV: "drawIncisalEdge", NEXT: "design" },
            meta: { title: "Draw tooth contour", category: "draw" }
        },

        design: {
            on: { PREV: "drawContour" },
            meta: { title: "Design smile", category: "design" }
        }
    }
})

const service = interpret(smileMachine)


export default new Vuex.Store({
    state: {
        currentState: service.initialState,

        extraOralImage: {
            name: 'extraOral',
            image: null,
            x: 0,
            y: 0,
            rotation: 0,
            scaleX: 1,
            scaleY: 1,
        },
        extraOralImageLoaded: false,

        intraOralImage: {
            name: 'intraOral',
            image: null,
            x: 0,
            y: 0,
            rotation: 0,
            scaleX: 0.125,
            scaleY: 0.125,
        },
        intraOralImageLoaded: false,

        referenceLength: '',

        zenithCoords: [],
        incisalEdgeCoords: [],

        activeContour: 0,
        contourCoords: [ [], [], [], [], [], [] ],
        planCoords: [],

        intraOralOpacity: 50,

        showReferenceLines: true,
        showOriginalCurvesAndAxes: false,
        showOriginalContours: true,
        showPlanPoints: true,
        showPlanBoundingBoxes: false,
    },
    getters: {
        isCategory: (state) => (name) => {
            const metadata = _mergeMetadata(state.currentState)
            return metadata.category === name
        },

        stateTitle: state => {
            const metadata = _mergeMetadata(state.currentState)
            return metadata.title
        },

        extraOralImageLoaded: state => {
            return state.extraOralImage.image !== null
        },

        intraOralImageLoaded: state => {
            return state.intraOralImage.image !== null
        },

        rulerCalibrated: state => {
            return Number.isInteger(state.referenceLength)
                && state.referenceLength >= 1
                && state.referenceLength <= 50
        },

    },
    mutations: {
        // State methods
        startMachine(state) {
            service.onTransition(machineState => {
                state.currentState = machineState

                if (machineState.matches('alignReferenceLine')) {
                    // make intraoral image visible
                    state.intraOralOpacity = 100
                } else if (machineState.matches('drawZenith')) {
                    state.showReferenceLines = false
                } else if (machineState.matches('design')) {
                    // copy contour to plan
                    state.planCoords.splice(0)
                    state.contourCoords.forEach((contour) => {
                        var points = []

                        contour.forEach((point) => {
                            points.push({ x: point.x, y: point.y })
                        })

                        state.planCoords.push(points)
                    })
                }
            }).start()
        },

        transition(state, action) {
            service.send(action)
        },

        // Images
        updateExtraOralImage(state, image) {
            Object.assign(state.extraOralImage, image)
        },

        updateIntraOralImage(state, image) {
            Object.assign(state.intraOralImage, image)
        },

        // Reference length
        updateReferenceLength(state, length) { state.referenceLength = length },

        // Controls
        updateIntraOralOpacity(state, value) { state.intraOralOpacity = value },

        showReferenceLines(state, value) { state.showReferenceLines = value },
        showOriginalCurvesAndAxes(state, value) { state.showOriginalCurvesAndAxes = value },
        showOriginalContours(state, value) { state.showOriginalContours = value },
        showPlanPoints(state, value) { state.showPlanPoints = value },
        showPlanBoundingBoxes(state, value) { state.showPlanBoundingBoxes = value },

        // Zenith / incisal edge coordinates
        addZenithCoord(state, coord) {
            if (state.zenithCoords.length < 6) {
                state.zenithCoords.push(coord)
            }
        },

        updateZenithCoord(state, payload) {
            Object.assign(state.zenithCoords[payload.index], {
                x: payload.x,
                y: payload.y
            })
        },

        addIncisalEdgeCoord(state, coord) {
            if (state.incisalEdgeCoords.length < 6) {
                state.incisalEdgeCoords.push(coord)
            }
        },

        updateIncisalEdgeCoord(state, payload) {
            Object.assign(state.incisalEdgeCoords[payload.index], {
                x: payload.x,
                y: payload.y
            })
        },

        // Contour coordinates
        updateActiveContour(state, index) {
            state.activeContour = index
        },

        addContourCoord(state, payload) {
            state.contourCoords[payload.tooth].push({
                x: payload.x,
                y: payload.y
            })
        },

        updateContourCoord(state, payload) {
            Object.assign(state.contourCoords[payload.tooth][payload.index], {
                x: payload.x,
                y: payload.y
            })
        },

        deleteContour(state, index) {
            _deleteContourCoords(state.contourCoords[index])
        },

        deleteAllContours(state) {
            for (let i = 0; i < state.contourCoords.length; i++) {
                _deleteContourCoords(state.contourCoords[i])
            }
        },


        updatePlanCoord(state, payload) {
            Object.assign(state.planCoords[payload.tooth][payload.index], {
                x: payload.x,
                y: payload.y
            })
        }
    }
})

function _mergeMetadata(state) {
    return Object.keys(state.meta).reduce((acc, key) => {
        const value = state.meta[key];

        // Assuming each meta value is an object
        Object.assign(acc, value);

        return acc;
    }, {});
}

function _deleteContourCoords(coords) {
    coords.splice(0)
}